
import EleosMap from '@/components/EleosMap.vue';
import { isNullOrUndefined } from '@/shared/helpers';
import { defineComponent } from 'vue';
import Truck from '@/types/Truck';
import { ActionTypes } from '@/store/actionTypes';
import NearbyTrailerResultsList from './NearbyTrailerResultsList.vue';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import SelectedLocationBubble from './SelectedLocationBubble.vue';
import TruckMapPoint from '@/types/TruckMapPoint';
import TrailerMapPoint from '@/types/TrailerMapPoint';
import BaseMapPoint from '@/types/BaseMapPoint';
import CustomerMapPoint from '@/types/CustomerMapPoint';
import FbaApiError from '@/types/FbaApiError';
import DirectionsSent from '@/components/DirectionsSent.vue';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import { NearbyTrailerResultType } from '@/types/NearbyTrailerResultType';
import DriverFeedback from '@/components/DriverFeedback.vue';
import Geopoint from '@/types/Geopoint';
import globalConstants from '@/shared/globalConstants';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import CustomerTrailerList from './CustomerTrailerList.vue';

export default defineComponent({
    name: 'FindTrailer',
    components: {
        EleosMap,
        NearbyTrailerResultsList,
        SelectedLocationBubble,
        DirectionsSent,
        SomethingWrongOverlay,
        DriverFeedback,
        CustomerTrailerList,
    },
    props: {
        latitude: Number,
        longitude: Number,
    },
    data(): {
        locationSelected: boolean;
        selectedResult: NearbyTrailerResult | null;
        isRefreshing: boolean;
        directionsModalShown: boolean;
        isViewingCustomerTrailers: boolean;
    } {
        return {
            locationSelected: false,
            selectedResult: null,
            isRefreshing: false,
            directionsModalShown: false,
            isViewingCustomerTrailers: false,
        };
    },
    computed: {
        ...mapGetters({
            isDetachError: 'isDetachError',
        }),
        customerTrailers(): NearbyTrailerResult[] {
            if (!this.selectedResult) return [];

            const copy = cloneDeep(this.selectedResult);
            return copy?.trailers.map((trailer) => {
                return new NearbyTrailerResult(
                    copy.distance,
                    copy.location.lat,
                    copy.locationAddress,
                    copy.locationName,
                    copy.customerId,
                    copy.customerCode,
                    copy.locationId,
                    copy.location.lng,
                    [trailer],
                    copy.locationPoolInfo,
                    NearbyTrailerResultType.standaloneTrailer,
                    copy.geofenceIds
                );
            });
        },
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
        hasError() {
            return this?.error?.message !== undefined;
        },
        loading() {
            return this.nearbyTrailerResults === undefined;
        },
        mapLatitude(): number {
            if (
                !isNullOrUndefined(this.latitude) &&
                !isNullOrUndefined(this.longitude)
            ) {
                return this.latitude as number;
            }

            return this.bestAvailableLocation
                ? this.bestAvailableLocation.lat
                : 39.5; // Geographic center of contiguous US
        },
        mapLongitude(): number {
            if (
                !isNullOrUndefined(this.latitude) &&
                !isNullOrUndefined(this.longitude)
            ) {
                return this.longitude as number;
            }

            return this.bestAvailableLocation
                ? this.bestAvailableLocation.lng
                : -98.35; // Geographic center of contiguous US
        },
        nearbyTrailerResults(): NearbyTrailerResult[] | undefined {
            return this.$store.getters.getNearbyTrailerResults;
        },
        truck(): Truck | undefined {
            return this.$store.getters.getTruck;
        },
        bestAvailableLocation(): Geopoint | undefined {
            return this.$store.getters.getPinnedBestAvailableLocation;
        },
        points(): BaseMapPoint[] {
            const mapPoints: BaseMapPoint[] = [];
            if (this.truck && this.bestAvailableLocation) {
                mapPoints.push(
                    new TruckMapPoint(this.bestAvailableLocation, this.truck)
                );
            }

            if (this.nearbyTrailerResults) {
                this.nearbyTrailerResults.forEach(
                    (result: NearbyTrailerResult) => {
                        if (
                            result.type ===
                            NearbyTrailerResultType.customerLocation
                        ) {
                            if (
                                result.trailers.filter(
                                    (x) => x.reservation?.isActive
                                ).length > 0
                            ) {
                                mapPoints.push(
                                    new TrailerMapPoint(result.location, result)
                                );
                            } else {
                                mapPoints.push(
                                    new CustomerMapPoint(
                                        result.location,
                                        result
                                    )
                                );
                            }
                        } else if (
                            result.type ===
                            NearbyTrailerResultType.standaloneTrailer
                        ) {
                            mapPoints.push(
                                new TrailerMapPoint(result.location, result)
                            );
                        }
                    }
                );
            }
            return mapPoints;
        },
        viewName(): string {
            return globalConstants.viewNames.findTrailer;
        },
    },
    methods: {
        onNavigateBackFromCustomerList() {
            this.isViewingCustomerTrailers = false;
        },
        onDisplayCustomerTrailers(value: boolean) {
            this.isViewingCustomerTrailers = value;
        },
        setSelectedResult(selectedResult: NearbyTrailerResult | null): void {
            this.selectedResult = selectedResult;
            this.locationSelected = true;
        },
        backToListView(): void {
            this.locationSelected = false;
            this.selectedResult = null;
        },
        navigationSent(): void {
            this.directionsModalShown = true;
        },
        hideDirectionsModal(): void {
            this.directionsModalShown = false;
        },
        async calculateRoutes(): Promise<void> {
            await this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_RESULTS_WITH_ROUTE_INFO,
                undefined
            );
        },
    },
    watch: {
        nearbyTrailerResults() {
            if (
                this.selectedResult &&
                !isNullOrUndefined(this.nearbyTrailerResults) &&
                this.nearbyTrailerResults.length > 0
            ) {
                this.selectedResult =
                    this.nearbyTrailerResults[
                        this.nearbyTrailerResults.findIndex(
                            (result) =>
                                result.location?.lat ===
                                    this.selectedResult?.location?.lat &&
                                result.location?.lng ===
                                    this.selectedResult?.location?.lng
                        )
                    ];
            }
        },
        async truck() {
            this.isRefreshing = true;
            await this.$store.dispatch(
                ActionTypes.FETCH_NEARBY_TRAILER_RESULTS,
                undefined
            );
            await this.calculateRoutes();
            this.isRefreshing = false;
        },
    },
    async mounted() {
        if (this.truck) {
            await this.$store.dispatch(
                ActionTypes.FETCH_NEARBY_TRAILER_RESULTS,
                undefined
            );
            await this.calculateRoutes();
        }
    },
});
